import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Input, Space, Tag, Tooltip, Popconfirm,
  message, Card, Row, Col, Badge
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, ThunderboltOutlined, RedoOutlined
} from "@ant-design/icons";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useEventBus, EventName } from "@/hooks/useEventBus";
import moment from "moment";
import { formatPMScheduleRuleSummary } from "../pmScheduleUtils";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";

const STATUS_COLOR = {
  true: "green",
  false: "default",
};

const PMPlan = () => {
  const navigate = useNavigate();
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const detailPath = (id) => buildFacilityPath(`pm-plan-detail/${id}`, facilityContext) || "/switch-view";
  const createPath = buildFacilityPath("create-pm-plan", facilityContext) || "/switch-view";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [triggering, setTriggering] = useState(null);
  const [ordering, setOrdering] = useState("-created_date");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (facilityId) params.append("facility", facilityId);
      if (searchText) params.append("search", searchText);
      if (ordering) params.append("ordering", ordering);
      const res = await api.filterPMPlan(params.toString());
      if (res.status === 200) {
        setData(res.data.results || []);
        setTotal(res.data.count || 0);
      }
    } catch {
      message.error("Failed to load PM Plans");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, facilityId, searchText, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEventBus(EventName.REFRESH_PM_PLAN_LIST, fetchData);

  const handleDelete = async (id) => {
    try {
      await api.deletePMPlan(id);
      message.success("Deleted successfully");
      fetchData();
    } catch {
      message.error("Failed to delete");
    }
  };

  const handleTrigger = async (id) => {
    try {
      setTriggering(id);
      const res = await api.triggerPMPlan(id);
      if (res.status === 201) {
        message.success(`PM Plan triggered! Workorder Wrapper #${res.data.id} created.`);
      }
    } catch {
      message.error("Failed to trigger PM Plan");
    } finally {
      setTriggering(null);
    }
  };

  const columns = [
    {
      title: "Plan Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: ordering === "name" ? "ascend" : ordering === "-name" ? "descend" : null,
      render: (text, record) => (
        <a onClick={() => navigate(detailPath(record.id))}>{text}</a>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v) => (
        <Badge
          status={v ? "processing" : "default"}
          text={v ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => (
        <span>
          {record.cycle_type ? (
            <Tag color="cyan">{formatPMScheduleRuleSummary(record)}</Tag>
          ) : (
            "-"
          )}
        </span>
      ),
    },
    {
      title: "Equipment Count",
      dataIndex: "equipment_names",
      key: "equipment_count",
      render: (names) => (
        <Tag color="blue">{Array.isArray(names) ? names.length : 0} equipment(s)</Tag>
      ),
    },
    {
      title: "Next schedule date",
      key: "next_schedule_date",
      render: (_, record) => {
        const nextScheduleDate = record.next_schedule_date;
        if (!nextScheduleDate) return "-";
        const isOverdue = moment(nextScheduleDate).isBefore(moment(), "day");
        return <Tag color={isOverdue ? "red" : "blue"}>{nextScheduleDate}</Tag>;
      },
    },
    {
      title: "Budget",
      dataIndex: "cost_budget",
      key: "cost_budget",
      sorter: true,
      sortOrder: ordering === "cost_budget" ? "ascend" : ordering === "-cost_budget" ? "descend" : null,
      render: (v) => (v ? `$${parseFloat(v).toFixed(2)}` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(detailPath(record.id))}
            />
          </Tooltip>
          <Tooltip title="Trigger Now (generate workorders)">
            <Popconfirm
              title="Manually trigger this PM Plan? This will generate a Workorder Wrapper immediately."
              onConfirm={() => handleTrigger(record.id)}
            >
              <Button
                size="small"
                icon={<ThunderboltOutlined />}
                loading={triggering === record.id}
                style={{ color: "#f59e0b", borderColor: "#f59e0b" }}
              />
            </Popconfirm>
          </Tooltip>
          <Popconfirm title="Delete this PM Plan?" onConfirm={() => handleDelete(record.id)}>
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <i className="fas fa-tasks" style={{ marginRight: 8, color: "#3A3F63" }} />
            PM Plans
          </span>
        }
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<RedoOutlined />} onClick={fetchData} loading={loading} />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(createPath)}
            >
              New PM Plan
            </Button>
          </Space>
        }
      >
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
        </Row>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 900 }}
          onChange={(_, __, sorter, extra) => {
            if (extra?.action === "sort" && sorter?.field) {
              const dir = sorter.order === "ascend" ? "" : "-";
              setOrdering(`${dir}${sorter.field}`);
              setPage(1);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
};

export default PMPlan;
